import { env } from './env.js';

/**
 * The API client. A thin wrapper over fetch that understands the response
 * envelope, refreshes an expired session once, and throws typed errors.
 *
 * There is no Axios here on purpose — this is about sixty lines of behaviour and
 * a dependency would be sixty lines plus a dependency.
 */

export class ApiError extends Error {
  constructor({ status, code, message, fields, retryAfterSeconds }) {
    super(message ?? 'Something went wrong.');
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** True when the failure is the user's input rather than the request itself. */
  get isValidation() {
    return this.code === 'VALIDATION_ERROR' && Boolean(this.fields);
  }

  get isOffline() {
    return this.code === 'NETWORK';
  }
}

/**
 * The routes a 401 must not be recovered from.
 *
 * Everything else — including `/auth/me` — gets a refresh attempt. That is why
 * this is a list rather than a prefix test on `/auth/`: the session read lives
 * under that prefix and runs on every page load and on the checkout, so
 * excluding the prefix meant the one request most likely to meet an expired
 * access token was the one request forbidden from repairing it. A signed-in
 * shopper silently became a guest fifteen minutes after their last click, most
 * visibly on the checkout page, with a valid refresh token still in the jar.
 *
 * The four that stay out are the ones where a 401 is the answer rather than a
 * problem: requesting a code, verifying a code, refreshing, and signing out.
 */
const NO_REFRESH = new Set([
  '/auth/otp/request',
  '/auth/otp/verify',
  '/auth/refresh',
  '/auth/logout',
]);

/**
 * Single-flight refresh.
 *
 * Five queries can fail with 401 in the same tick. Without this lock all five
 * would call /auth/refresh, and because refresh tokens rotate, four of them
 * would present an already-rotated token and trip reuse detection — signing the
 * customer out for doing nothing wrong.
 */
let refreshInFlight = null;

const refreshSession = async () => {
  refreshInFlight =
    refreshInFlight ??
    fetch(`${env.apiUrl}/auth/refresh`, { method: 'POST', credentials: 'include' })
      .then((response) => response.ok)
      .catch(() => false)
      .finally(() => {
        // Cleared on the next tick so callers awaiting this promise all see the
        // same result before a new attempt can start.
        setTimeout(() => {
          refreshInFlight = null;
        }, 0);
      });

  return refreshInFlight;
};

const listeners = new Set();

/** Lets the auth store react to a session that could not be recovered. */
export const onSessionExpired = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const buildUrl = (path, params) => {
  const url = new URL(`${env.apiUrl}${path}`);

  for (const [key, value] of Object.entries(params ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const entry of value) url.searchParams.append(key, String(entry));
    } else {
      url.searchParams.set(key, String(value));
    }
  }

  return url.toString();
};

const parse = async (response) => {
  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;

  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError({
      status: response.status,
      code: 'MALFORMED_RESPONSE',
      message: 'We got an unexpected answer from the server. Try again in a moment.',
    });
  }
};

const request = async (path, { method = 'GET', body, params, signal, retryOn401 = true, raw = false } = {}) => {
  let response;

  try {
    response = await fetch(buildUrl(path, params), {
      method,
      credentials: 'include',
      signal,
      headers: {
        ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        Accept: raw ? '*/*' : 'application/json',
      },
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    throw new ApiError({
      status: 0,
      code: 'NETWORK',
      message: 'We could not reach the store. Check your connection and try again.',
    });
  }

  /**
   * A 401 is attempted-then-recovered, never read from the error code.
   *
   * The tempting shortcut is to refresh only when the server says
   * `TOKEN_EXPIRED` and to skip it on `NO_SESSION`, sparing a first-time visitor
   * a pointless round trip on the front page. It does not work: the access
   * cookie carries a `maxAge`, so the browser deletes it the moment it expires
   * and the request that follows carries no token at all. The server cannot tell
   * that apart from a first visit and answers `NO_SESSION` to both — which is
   * exactly the case this needs to repair.
   *
   * So every 401 outside `NO_REFRESH` gets one attempt. It costs a guest one
   * failed refresh per session; the alternative is turning signed-in shoppers
   * back into guests fifteen minutes after their last click.
   */
  if (response.status === 401 && retryOn401 && !NO_REFRESH.has(path.split('?')[0])) {
    const recovered = await refreshSession();

    if (recovered) return request(path, { method, body, params, signal, retryOn401: false, raw });

    for (const listener of listeners) listener();
  }

  if (raw) {
    if (!response.ok) {
      const envelope = await parse(response);
      throw new ApiError({
        status: response.status,
        code: envelope?.error?.code ?? 'REQUEST_FAILED',
        message: envelope?.error?.message,
      });
    }
    return response;
  }

  const envelope = await parse(response);

  if (!response.ok || envelope?.success === false) {
    throw new ApiError({
      status: response.status,
      code: envelope?.error?.code ?? 'REQUEST_FAILED',
      message: envelope?.error?.message,
      fields: envelope?.error?.fields,
      retryAfterSeconds: envelope?.error?.retryAfterSeconds,
    });
  }

  // Meta is attached rather than returned separately so a single TanStack Query
  // cache entry holds both the rows and their pagination.
  if (envelope && envelope.meta !== undefined && envelope.data !== null && typeof envelope.data === 'object') {
    return Array.isArray(envelope.data)
      ? Object.assign([...envelope.data], { meta: envelope.meta })
      : { ...envelope.data, meta: envelope.meta };
  }

  return envelope?.data ?? null;
};

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
  raw: (path, options) => request(path, { ...options, raw: true }),
};

export default api;
